import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shared Landing Page Component
 *
 * Used by all industry landing pages (hospitality, retail, logistics, etc.)
 * Handles onboarding dialog state and URL management.
 */
import { useState, useEffect } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { LandingAppBar } from './landingAppBar';
import { HeroSection } from './heroSection';
import { PrivacySection } from './privacySection';
import { PricingSection } from './pricingSection';
import { FAQSection } from './faqSection';
import { LandingFooter } from './landingFooter';
import { HowItWorksSection } from './howItWorksSection';
import { TestimonialsSection } from './testimonialsSection';
import { FeaturesSection } from './featuresSection';
import { BookDemoSection } from './bookDemoSection';
import { CostOfHiringSection } from './costOfHiringSection';
import { RoiCalculatorSection } from './roiCalculatorSection';
import { PositioningSection } from './positioningSection';
import { StickyCtaFooter } from './stickyCtaFooter';
import { MobileOnboardingDialog } from './mobileOnboardingDialog';
import { DesktopOnboardingDialog } from './desktopOnboardingDialog';
export const LandingPage = ({ industry, appBar, hero, privacy, pricing, faq, footer, howItWorks, testimonials, features, bookDemo, costOfHiring, roiCalculator, positioning, }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // Check if we're on the /onboarding path
    const isOnboardingPath = window.location.pathname === '/onboarding';
    // Mobile onboarding only (desktop goes directly to scoring app)
    const [showMobileOnboarding, setShowMobileOnboarding] = useState(isOnboardingPath && isMobile);
    const [showDesktopOnboarding, setShowDesktopOnboarding] = useState(isOnboardingPath && !isMobile);
    // Get industry key for URL param
    const industryKey = industry.toLowerCase();
    // Update URL when dialog state changes
    const openOnboarding = () => {
        if (isMobile) {
            setShowMobileOnboarding(true);
            // Update URL without reload
            window.history.pushState({}, '', `/onboarding?industry=${industryKey}`);
        }
        else {
            setShowDesktopOnboarding(true);
            window.history.pushState({}, '', `/onboarding?industry=${industryKey}`);
        }
    };
    const closeOnboarding = () => {
        setShowMobileOnboarding(false);
        setShowDesktopOnboarding(false);
        // Go back to wherever user came from
        window.history.back();
    };
    // Handle browser back button
    useEffect(() => {
        const handlePopState = () => {
            const isOnboarding = window.location.pathname === '/onboarding';
            if (isMobile) {
                setShowMobileOnboarding(isOnboarding);
            }
            else {
                setShowDesktopOnboarding(isOnboarding);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isMobile]);
    return (_jsxs(Box, { children: [_jsx(LandingAppBar, { ...appBar, onCtaClick: openOnboarding }), _jsx(HeroSection, { ...hero, onPrimaryCtaClick: openOnboarding }), _jsx(HowItWorksSection, { ...howItWorks }), _jsx(TestimonialsSection, { ...testimonials }), costOfHiring && _jsx(CostOfHiringSection, { ...costOfHiring }), roiCalculator && _jsx(RoiCalculatorSection, { ...roiCalculator }), positioning && _jsx(PositioningSection, { ...positioning }), features && _jsx(FeaturesSection, { ...features }), _jsx(PrivacySection, { ...privacy }), _jsx(PricingSection, { ...pricing, onCtaClick: openOnboarding }), _jsx(FAQSection, { ...faq }), bookDemo && _jsx(BookDemoSection, { ...bookDemo }), _jsx(LandingFooter, { ...footer }), _jsx(StickyCtaFooter, { ctaHref: "#", onCtaClick: openOnboarding }), _jsx(MobileOnboardingDialog, { open: showMobileOnboarding, onClose: closeOnboarding, industry: industry }), _jsx(DesktopOnboardingDialog, { open: showDesktopOnboarding, onClose: closeOnboarding, industry: industry })] }));
};
